import React, { useState, useEffect, useRef } from 'react';
import Controls from './Controls';

const VideoCall = ({ 
  conversation, 
  currentUser, 
  onEndCall,
  isIncoming = false,
  roomId = null
}) => {
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  
  const localVideoRef = useRef(null);
  const socket = useRef(null);
  const peerConnections = useRef({});
  const screenStream = useRef(null);
  
  // Initialize WebSocket connection and local media stream
  useEffect(() => {
    const initCall = async () => {
      try {
        // Initialize local stream with fallbacks
        await initLocalStream();
        
        // Connect to signaling server
        connectToSignalingServer();
      } catch (error) {
        console.error('Error initializing call:', error);
      }
    };
    
    initCall();
    
    // Cleanup on unmount
    return () => {
      // Close all peer connections
      Object.values(peerConnections.current).forEach(pc => pc.close());
      
      // Close WebSocket connection
      if (socket.current && socket.current.readyState === WebSocket.OPEN) {
        socket.current.close();
      }
      
      // Stop local stream tracks
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
      
      // Stop screen sharing if active
      if (screenStream.current) {
        screenStream.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);
  
  // Initialize local media stream
  const initLocalStream = async () => {
    try {
      // First check if media devices are available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Media devices API not available in this browser');
      }
      
      // Try to get both audio and video
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true
        });
        
        setLocalStream(stream);
        
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
      } catch (mediaError) {
        console.warn('Could not access both camera and microphone, trying audio only:', mediaError);
        
        // Try audio only as fallback
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false
          });
          
          setLocalStream(audioStream);
          setIsVideoOff(true);
          
          alert('Camera access was denied or not available. Joining with audio only.');
        } catch (audioError) {
          console.error('Could not access microphone either:', audioError);
          alert('Could not access microphone. You will be in view-only mode.');
          
          // Create empty stream as a last resort
          const emptyStream = new MediaStream();
          setLocalStream(emptyStream);
          setIsAudioMuted(true);
          setIsVideoOff(true);
        }
      }
    } catch (error) {
      console.error('Error accessing media devices:', error);
      alert('Could not access camera or microphone. Please check permissions and make sure you are using a secure (HTTPS) connection or localhost.');
    }
  };
  
  // Connect to signaling server
  const connectToSignalingServer = () => {
    const callRoomId = roomId || `czone-${conversation.id}-${Date.now()}`;
    const wsUrl = `ws://localhost:8000/ws/${callRoomId}/${currentUser.id}?name=${encodeURIComponent(currentUser.name)}`;
    
    socket.current = new WebSocket(wsUrl);
    
    socket.current.onopen = () => {
      console.log('Connected to signaling server');
      setIsConnected(true);
    };
    
    socket.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      handleSignalingMessage(message);
    };
    
    socket.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    socket.current.onclose = () => {
      console.log('Disconnected from signaling server');
      setIsConnected(false);
    };
  };
  
  // Handle incoming signaling messages
  const handleSignalingMessage = async (message) => {
    const { type, userId, sdp, candidate, name, participants: roomParticipants, chat } = message;
    
    switch (type) {
      case 'user-joined':
        console.log(`User joined: ${userId} (${name})`);
        await createPeerConnection(userId, name, true);
        break;
        
      case 'user-left':
        console.log(`User left: ${userId}`);
        closePeerConnection(userId);
        break;
        
      case 'offer':
        await handleOffer(userId, sdp);
        break;
        
      case 'answer':
        await handleAnswer(userId, sdp);
        break;
        
      case 'ice-candidate':
        handleIceCandidate(userId, candidate);
        break;
        
      case 'participants-list':
        console.log('Received participants list:', roomParticipants);
        setParticipants(roomParticipants);
        
        // Create peer connections for existing participants
        for (const participant of roomParticipants) {
          if (participant.id !== currentUser.id && !peerConnections.current[participant.id]) {
            await createPeerConnection(participant.id, participant.name, false);
          }
        }
        break;
        
      case 'chat-message':
        handleChatMessage(chat);
        break;
        
      default:
        console.log('Unknown message type:', type);
    }
  };
  
  // Create a new peer connection for a user
  const createPeerConnection = async (userId, userName, isInitiator) => {
    try {
      // ICE servers configuration (STUN/TURN)
      const iceServers = [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
        // Add TURN servers for production
      ];
      
      const pc = new RTCPeerConnection({ iceServers });
      peerConnections.current[userId] = pc;
      
      // Add local tracks to the peer connection
      if (localStream) {
        localStream.getTracks().forEach(track => {
          pc.addTrack(track, localStream);
        });
      }
      
      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendToServer({
            type: 'ice-candidate',
            userId: userId,
            candidate: event.candidate
          });
        }
      };
      
      // Handle connection state changes
      pc.onconnectionstatechange = () => {
        console.log(`Connection state change: ${pc.connectionState}`);
      };
      
      // Handle incoming tracks
      pc.ontrack = (event) => {
        console.log('Received remote track from:', userId);
        setRemoteStreams(prev => ({
          ...prev,
          [userId]: {
            stream: event.streams[0],
            name: userName
          }
        }));
      };
      
      // If we're the initiator, create and send an offer
      if (isInitiator) {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        
        sendToServer({
          type: 'offer',
          userId: userId,
          sdp: pc.localDescription
        });
      }
      
      return pc;
    } catch (error) {
      console.error('Error creating peer connection:', error);
      return null;
    }
  };
  
  // Handle incoming offer
  const handleOffer = async (userId, sdp) => {
    try {
      const pc = peerConnections.current[userId] || 
                await createPeerConnection(userId, 'Unknown', false);
      
      if (!pc) return;
      
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      sendToServer({
        type: 'answer',
        userId: userId,
        sdp: pc.localDescription
      });
    } catch (error) {
      console.error('Error handling offer:', error);
    }
  };
  
  // Handle incoming answer
  const handleAnswer = async (userId, sdp) => {
    try {
      const pc = peerConnections.current[userId];
      if (!pc) return;
      
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
    } catch (error) {
      console.error('Error handling answer:', error);
    }
  };
  
  // Handle incoming ICE candidate
  const handleIceCandidate = (userId, candidate) => {
    try {
      const pc = peerConnections.current[userId];
      if (!pc) return;
      
      pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      console.error('Error handling ICE candidate:', error);
    }
  };
  
  // Close peer connection for a user
  const closePeerConnection = (userId) => {
    const pc = peerConnections.current[userId];
    if (!pc) return;
    
    pc.close();
    delete peerConnections.current[userId];
    
    setRemoteStreams(prev => {
      const newStreams = { ...prev };
      delete newStreams[userId];
      return newStreams;
    });
    
    setParticipants(prev => prev.filter(p => p.id !== userId));
  };
  
  // Send message to signaling server
  const sendToServer = (message) => {
    if (socket.current && socket.current.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify(message));
    }
  };
  
  // Handle chat messages
  const handleChatMessage = (chat) => {
    setMessages(prev => [...prev, chat]);
  };
  
  // Send chat message
  const sendChatMessage = () => {
    if (!newMessage.trim()) return;
    
    const chatMessage = {
      content: newMessage,
      sender: currentUser.name,
      senderId: currentUser.id,
      timestamp: new Date().toISOString()
    };
    
    sendToServer({
      type: 'chat',
      chat: chatMessage
    });
    
    setMessages(prev => [...prev, chatMessage]);
    setNewMessage('');
  };
  
  // Toggle audio mute
  const toggleAudio = () => {
    if (localStream) {
      const audioTracks = localStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsAudioMuted(!isAudioMuted);
    }
  };
  
  // Toggle video
  const toggleVideo = () => {
    if (localStream) {
      const videoTracks = localStream.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsVideoOff(!isVideoOff);
    }
  };
  
  // Toggle screen sharing
  const toggleScreenShare = async () => {
    try {
      if (isSharingScreen) {
        // Stop screen sharing
        if (screenStream.current) {
          screenStream.current.getTracks().forEach(track => track.stop());
        }
        
        // Replace screen track with camera track
        if (localStream) {
          const videoTracks = localStream.getVideoTracks();
          if (videoTracks.length > 0) {
            // Replace tracks in all peer connections
            Object.values(peerConnections.current).forEach(pc => {
              const senders = pc.getSenders();
              const sender = senders.find(s => s.track && s.track.kind === 'video');
              if (sender) {
                sender.replaceTrack(videoTracks[0]);
              }
            });
            
            // Update local video
            if (localVideoRef.current) {
              localVideoRef.current.srcObject = localStream;
            }
          }
        }
        
        setIsSharingScreen(false);
      } else {
        // Start screen sharing
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        screenStream.current = stream;
        
        // Replace camera track with screen track in all peer connections
        const screenVideoTrack = stream.getVideoTracks()[0];
        
        Object.values(peerConnections.current).forEach(pc => {
          const senders = pc.getSenders();
          const sender = senders.find(s => s.track && s.track.kind === 'video');
          if (sender) {
            sender.replaceTrack(screenVideoTrack);
          }
        });
        
        // Update local video
        if (localVideoRef.current) {
          const newStream = new MediaStream([screenVideoTrack]);
          if (localStream) {
            const audioTracks = localStream.getAudioTracks();
            if (audioTracks.length > 0) {
              newStream.addTrack(audioTracks[0]);
            }
          }
          localVideoRef.current.srcObject = newStream;
        }
        
        // Handle screen sharing stop event
        screenVideoTrack.onended = () => {
          toggleScreenShare();
        };
        
        setIsSharingScreen(true);
      }
    } catch (error) {
      console.error('Error toggling screen share:', error);
    }
  };
  
  // End the call
  const leaveMeeting = () => {
    // Close WebSocket connection
    if (socket.current) {
      socket.current.close();
    }
    
    // Stop all media tracks
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    
    if (screenStream.current) {
      screenStream.current.getTracks().forEach(track => track.stop());
    }
    
    // Notify parent component
    onEndCall();
  };
  
  return (
    <div className="video-call-container">
      <div className="video-call-header">
        <div className="call-logo">
          <img src="/cdezone_logo.jpg" alt="CZone Logo" className="czone-logo-small" />
          <h2>{conversation ? conversation.name : 'CZone Video Call'}</h2>
        </div>
        <div className="call-actions">
          <button 
            className={`icon-button ${showChat ? 'active' : ''}`} 
            onClick={() => setShowChat(!showChat)}
            aria-label="Toggle chat"
          >
            <i className="fas fa-comment"></i>
          </button>
        </div>
      </div>
      
      <div className={`video-call-content ${showChat ? 'with-chat' : ''}`}>
        <div className="video-grid">
          {/* Local video */}
          <div className="video-container local-video">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className={isVideoOff ? 'hidden' : ''}
            />
            {isVideoOff && (
              <div className="video-placeholder">
                <div className="avatar-container">
                  <img 
                    src={currentUser.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.name)}&background=random`}
                    alt={currentUser.name}
                    className="avatar"
                  />
                </div>
                <div className="name-tag">{currentUser.name} (You)</div>
              </div>
            )}
            <div className="video-overlay">
              <div className="name-tag">
                {currentUser.name} {isSharingScreen ? '(Screen)' : '(You)'}
              </div>
              <div className="status-indicators">
                {isAudioMuted && <i className="fas fa-microphone-slash"></i>}
                {isVideoOff && <i className="fas fa-video-slash"></i>}
              </div>
            </div>
          </div>
          
          {/* Remote videos */}
          {Object.entries(remoteStreams).map(([userId, { stream, name }]) => (
            <div key={userId} className="video-container remote-video">
              <video
                autoPlay
                playsInline
                srcObject={stream}
              />
              <div className="video-overlay">
                <div className="name-tag">{name}</div>
              </div>
            </div>
          ))}
        </div>
        
        {showChat && (
          <div className="call-chat">
            <div className="chat-messages">
              {messages.map((msg, index) => (
                <div 
                  key={index} 
                  className={`chat-message ${msg.senderId === currentUser.id ? 'sent' : 'received'}`}
                >
                  <div className="message-sender">{msg.sender}</div>
                  <div className="message-content">{msg.content}</div>
                  <div className="message-time">
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))}
            </div>
            <div className="chat-input">
              <input
                type="text"
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
              />
              <button onClick={sendChatMessage}>
                <i className="fas fa-paper-plane"></i>
              </button>
            </div>
          </div>
        )}
      </div>
      
      <div className="video-call-controls">
        <Controls
          isAudioMuted={isAudioMuted}
          isVideoOff={isVideoOff}
          isSharingScreen={isSharingScreen}
          toggleAudio={toggleAudio}
          toggleVideo={toggleVideo}
          toggleScreenShare={toggleScreenShare}
          leaveMeeting={leaveMeeting}
        />
      </div>
    </div>
  );
};

export default VideoCall;
